import { useCallback, useEffect, useState } from "react";
import "./App.css";
import DataTable from "./components/DataTable";
import FilterControls from "./components/FilterControls";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeToggle } from "./components/ThemeToggle";
import { useDataLoader } from "./hooks/useDataLoader";
import { useDataFiltering } from "./hooks/useDataFiltering";
import { useDebounce } from "./hooks/useDebounce";
import {
  MONTH_NAMES,
  NORTHERN,
  DESC_PRICE,
  type Hemisphere,
  type SortType,
  type MonthName,
  type FilterType,
  type ScheduleFilter,
} from "./types";

function App(): JSX.Element {
  const { data, headerData, loading, error } = useDataLoader();

  const [hemisphere, setHemisphere] = useState<Hemisphere>(NORTHERN);
  const [currentSort, setCurrentSort] = useState<SortType>(DESC_PRICE);
  const [currentMonth, setCurrentMonth] = useState<MonthName | "AllMonths">(
    "AllMonths",
  );
  const [currentFilter, setCurrentFilter] = useState<FilterType>("All");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [scheduleFilter, setScheduleFilter] = useState<ScheduleFilter>("");

  // Debounce search term for better performance
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Use custom filtering hook
  const { filteredData } = useDataFiltering({
    data,
    currentSort,
    currentMonth,
    currentFilter,
    searchTerm: debouncedSearchTerm,
    hemisphere,
  });

  // Initialize current month on component mount
  useEffect(() => {
    const currentDate = new Date();
    setCurrentMonth(MONTH_NAMES[currentDate.getMonth()]);
  }, []);

  // Memoized event handlers to prevent unnecessary re-renders
  const handleHemisphereChange = useCallback(
    (newHemisphere: Hemisphere): void => {
      setHemisphere(newHemisphere);
    },
    [],
  );

  const handleSortChange = useCallback((sortType: SortType): void => {
    setCurrentSort(sortType);
  }, []);

  const handleMonthChange = useCallback(
    (month: MonthName | "AllMonths"): void => {
      setCurrentMonth(month);
    },
    [],
  );

  const handleFilterChange = useCallback((filterType: FilterType): void => {
    setCurrentFilter(filterType);
  }, []);

  const handleSearchChange = useCallback((term: string): void => {
    setSearchTerm(term);
  }, []);

  const handleScheduleFilterChange = useCallback(
    (schedule: ScheduleFilter): void => {
      setScheduleFilter(schedule === scheduleFilter ? "" : schedule);
    },
    [scheduleFilter],
  );

  // Error state
  if (error) {
    return (
      <div className="error-container">
        <div className="error-card">
          <div className="error-icon">⚠️</div>
          <div className="error-title">Failed to Load Data</div>
          <div className="error-message">{error}</div>
          <button
            className="btn-modern"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <div className="loading-text">Loading ACNH Collectibles Data...</div>
        <div className="loading-subtitle">
          Preparing your island collection...
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="App">
        {/* Modern Header */}
        <header className="app-header">
          <div className="header-content">
            <div className="header-top">
              <div className="header-text">
                <h1>Animal Crossing New Horizons</h1>
                <p className="app-subtitle">Collectibles Data Lookup</p>
              </div>
              <ThemeToggle size="md" className="theme-toggle-header" />
            </div>
            <div className="header-stats">
              <span className="stat-item">
                <span className="stat-number">{filteredData.length}</span>
                <span className="stat-label">items found</span>
              </span>
              <span className="stat-item">
                <span className="stat-number">{data.length}</span>
                <span className="stat-label">total items</span>
              </span>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="app-content">
          {/* Filter Controls */}
          <div style={{ marginBottom: "var(--space-xl)" }}>
            <FilterControls
              hemisphere={hemisphere}
              currentSort={currentSort}
              currentMonth={currentMonth}
              currentFilter={currentFilter}
              searchTerm={searchTerm}
              scheduleFilter={scheduleFilter}
              onHemisphereChange={handleHemisphereChange}
              onSortChange={handleSortChange}
              onMonthChange={handleMonthChange}
              onFilterChange={handleFilterChange}
              onSearchChange={handleSearchChange}
              onScheduleFilterChange={handleScheduleFilterChange}
              showScheduleButtons={currentMonth !== "AllMonths"}
            />
          </div>

          {/* Results Summary */}
          <div className="results-summary">
            {filteredData.length === 0 && !loading ? (
              <div className="no-results">
                <div className="no-results-icon">🔍</div>
                <div className="no-results-title">No items found</div>
                <div className="no-results-message">
                  Try adjusting your filters or search terms
                </div>
              </div>
            ) : (
              <div className="results-info">
                Showing <strong>{filteredData.length}</strong> of{" "}
                <strong>{data.length}</strong> items
                {currentMonth !== "AllMonths" && (
                  <span className="filter-badge">📅 {currentMonth}</span>
                )}
                {currentFilter !== "All" && (
                  <span className="filter-badge">🏷️ {currentFilter}</span>
                )}
                {debouncedSearchTerm && (
                  <span className="filter-badge">
                    🔍 "{debouncedSearchTerm}"
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Data Table */}
          <DataTable
            data={filteredData}
            headerData={headerData}
            hemisphere={hemisphere}
            currentMonth={currentMonth}
            scheduleFilter={scheduleFilter}
          />
        </main>
      </div>
    </ErrorBoundary>
  );
}

export default App;
